import type { Row, Table } from "@tanstack/react-table";
import { ChevronRight } from "lucide-react";
import { type MouseEvent, type ReactNode, useCallback, useState } from "react";
import { toast } from "sonner";

import type { Matter } from "@/shared/cache/slices/matters";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import type { Entry } from "@/shared/types/entries";
import { EntryCard } from "@/v2/components/EntryCard";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import {
	classifyEntry,
	DEFAULT_FILTER,
	type FilterState,
} from "@/v2/components/FilterPopover";
import { SwipeableCard } from "@/v2/components/SwipeableCard";
import { useActions } from "@/v2/contexts/ActionQueueProvider";
import { formatDateForApi } from "@/v2/lib/dates";
import { createEditEntryPayload } from "@/v2/lib/entryPayloads";
import { cn } from "@/v2/lib/utils";
import { EditEntryDialog } from "./EditEntryDialog";
import { EntriesEmptyState } from "./EntriesEmptyState";
import { EntriesLoadingSkeleton } from "./EntriesLoadingSkeleton";

export interface EntriesListProps {
	isLoading: boolean;
	entries: Entry[];
	dateFilteredEntries: Entry[];
	table: Table<Entry>;
	isMultiDate: boolean;
	selectionMode: boolean;
	matters?: Matter[];
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
	onIconClick: (entry: Entry, event: MouseEvent) => void;
	onRowSelect: (entry: Entry, event: MouseEvent) => void;
	onLongPress: (entry: Entry) => void;
	onFilterChange: (filter: FilterState) => void;
	onEntriesRendered: (node: HTMLDivElement | null) => void;
}

export function EntriesList({
	isLoading,
	entries,
	dateFilteredEntries,
	table,
	isMultiDate,
	selectionMode,
	matters,
	getRestrictedWords,
	onIconClick,
	onRowSelect,
	onLongPress,
	onFilterChange,
	onEntriesRendered,
}: EntriesListProps) {
	const actions = useActions();
	const [editEntry, setEditEntry] = useState<Entry | null>(null);
	const handleEditSubmit = useCallback(
		(entry: Entry, values: TimeEntryFormValues) => {
			actions.editEntry(
				createEditEntryPayload(
					entry.id,
					values,
					formatDateForApi(values.date),
					{
						timekeeperId: entry.timekeeperId,
					},
				),
			);
			setEditEntry(null);
		},
		[actions],
	);

	const handleEditExport = useCallback(
		(entry: Entry, values: TimeEntryFormValues) => {
			actions.editThenExport(
				createEditEntryPayload(
					entry.id,
					values,
					formatDateForApi(values.date),
					{
						timekeeperId: entry.timekeeperId,
					},
				),
			);
			setEditEntry(null);
		},
		[actions],
	);

	const handleResolveFeedback = useCallback(
		(entryId: number) => {
			actions.resolveFeedback(entryId);
			setEditEntry(null);
		},
		[actions],
	);

	const handleEditOpenChange = useCallback((open: boolean) => {
		if (!open) setEditEntry(null);
	}, []);

	const handleExportEntry = useCallback(
		(entry: Entry) => {
			actions.exportEntries([entry.id], entry.timekeeperId);
		},
		[actions],
	);

	const handleDeleteEntry = useCallback(
		(entry: Entry) => {
			actions.deleteEntries([entry.id], entry.timekeeperId);
		},
		[actions],
	);

	const handleDuplicateEntry = useCallback(
		(entry: Entry) => {
			actions.duplicateEntries([entry.id], entry.timekeeperId);
		},
		[actions],
	);

	const handleResetFilters = useCallback(() => {
		onFilterChange(DEFAULT_FILTER);
	}, [onFilterChange]);

	const toggleGroup = useCallback(
		(row: Row<Entry>) => {
			table.setExpanded((previous) => {
				if (previous === true) {
					const next: Record<string, boolean> = {};
					for (const preExpandedRow of table.getPreExpandedRowModel()
						.flatRows) {
						if (preExpandedRow.getIsGrouped() && preExpandedRow.id !== row.id) {
							next[preExpandedRow.id] = true;
						}
					}
					return next;
				}

				const next = { ...(previous ?? {}) };
				if (next[row.id]) delete next[row.id];
				else next[row.id] = true;
				return next;
			});
		},
		[table],
	);

	const renderGroupHeader = useCallback(
		(row: Row<Entry>) => {
			const isExpanded = row.getIsExpanded();
			const label = String(row.groupingValue ?? "Unknown");

			return (
				<button
					key={row.id}
					type="button"
					aria-expanded={isExpanded}
					onClick={() => toggleGroup(row)}
					className="sticky top-[49px] z-10 flex w-full items-center border-b border-border bg-secondary/95 px-4 py-1.5 text-left text-xs font-medium text-muted-foreground backdrop-blur-sm transition-colors hover:bg-secondary"
				>
					<span className="flex min-w-0 items-center gap-1.5">
						<ChevronRight
							className={cn(
								"size-3.5 shrink-0 transition-transform duration-150 ease-out",
								isExpanded && "rotate-90",
							)}
						/>
						<span className="truncate">{label}</span>
					</span>
				</button>
			);
		},
		[toggleGroup],
	);

	const renderEntryRow = useCallback(
		(row: Row<Entry>) => {
			const entry = row.original;
			return (
				<SwipeableCard
					key={`${row.id}-${actions.failedExports.has(entry.id) ? 1 : 0}`}
					canSwipeRight={() => {
						const status = classifyEntry(entry, matters, getRestrictedWords);

						if (status === "incomplete") {
							toast.error(
								"Entry is incomplete — fill in all required fields before exporting.",
							);
							return false;
						}

						return true;
					}}
					onSwipeRight={() => handleExportEntry(entry)}
					onSwipeLeft={() => handleDeleteEntry(entry)}
					disabled={selectionMode || actions.pendingEntryIds.has(entry.id)}
				>
					<EntryCard
						entry={entry}
						onClick={setEditEntry}
						selectionMode={selectionMode}
						selected={row.getIsSelected()}
						onIconClick={onIconClick}
						onRowSelect={onRowSelect}
						onLongPress={onLongPress}
						exportFailed={actions.failedExports.has(entry.id)}
						isMutating={actions.pendingEntryIds.has(entry.id)}
					/>
				</SwipeableCard>
			);
		},
		[
			actions.failedExports,
			actions.pendingEntryIds,
			getRestrictedWords,
			handleDeleteEntry,
			handleExportEntry,
			matters,
			onIconClick,
			onLongPress,
			onRowSelect,
			selectionMode,
		],
	);

	const renderRows = useCallback(
		(rows: Row<Entry>[]): ReactNode =>
			rows.map((row) =>
				row.getIsGrouped() ? renderGroupHeader(row) : renderEntryRow(row),
			),
		[renderEntryRow, renderGroupHeader],
	);

	let content: ReactNode;

	if (isLoading) {
		content = <EntriesLoadingSkeleton />;
	} else if (table.getFilteredRowModel().flatRows.length === 0) {
		content = (
			<EntriesEmptyState
				totalEntryCount={entries.length}
				dateFilteredCount={dateFilteredEntries.length}
				isMultiDate={isMultiDate}
				onResetFilters={handleResetFilters}
			/>
		);
	} else {
		content = (
			<div ref={onEntriesRendered}>{renderRows(table.getRowModel().rows)}</div>
		);
	}

	return (
		<>
			{content}
			<EditEntryDialog
				entry={editEntry}
				failedExports={actions.failedExports}
				onOpenChange={handleEditOpenChange}
				onResolveFeedback={handleResolveFeedback}
				onSubmit={handleEditSubmit}
				onExport={handleEditExport}
				onDuplicate={(entry) => {
					handleDuplicateEntry(entry);
					setEditEntry(null);
				}}
				onDelete={(entry) => {
					handleDeleteEntry(entry);
					setEditEntry(null);
				}}
				onRestore={(entry) => {
					actions.setStatus(entry.id, "tracked", undefined, entry.timekeeperId);
					setEditEntry(entry);
				}}
			/>
		</>
	);
}
